import { useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Modal, Button, Checkbox } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import { SuccessToast } from "app/pages/sales-intelligence/common-components/SuccessToast/SuccessToast";
import { Textarea } from "app/pages/sales-intelligence/common-components/Textarea/Textarea";
import useContactIssueMutation from "app/pages/sales-intelligence/sub-modules/contacts-search/query/mutations/useContactIssueMutation";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";
import type {
    ContactIssuePayload,
    ContactSearchEntry,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import { ContactReportReason } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/consts";

type Props = {
    isOpen: boolean;
    contact: ContactSearchEntry;
    onClose(): void;
    onSubmit(): void;
};

type ReportReasonConfig = { reason: ContactReportReason; labelKey: string };

const REVEALED_ONLY_REASONS: ReportReasonConfig[] = [
    {
        reason: ContactReportReason.EmailIncorrect,
        labelKey: "si.contacts_search.contacts.report.reasons.email_incorrect",
    },
    {
        reason: ContactReportReason.MobileIncorrect,
        labelKey: "si.contacts_search.contacts.report.reasons.mobile_incorrect",
    },
    {
        reason: ContactReportReason.DirectIncorrect,
        labelKey: "si.contacts_search.contacts.report.reasons.direct_incorrect",
    },
];

const COMMON_REASONS: ReportReasonConfig[] = [
    {
        reason: ContactReportReason.CompanyIncorrect,
        labelKey: "si.contacts_search.contacts.report.reasons.company_incorrect",
    },
    {
        reason: ContactReportReason.RoleIncorrect,
        labelKey: "si.contacts_search.contacts.report.reasons.role_incorrect",
    },
    {
        reason: ContactReportReason.Other,
        labelKey: "si.contacts_search.contacts.report.reasons.other",
    },
];

const TOAST_DURATION_MS = 3000;

const ContactIssueModal = ({ isOpen, contact, onClose, onSubmit }: Props) => {
    const dispatch = useDispatch();
    const [selectedReasons, setSelectedReasons] = useState<ContactReportReason[]>([]);
    const [additionalInfo, setAdditionalInfo] = useState("");

    const { mutate: submitContactIssue } = useContactIssueMutation({
        onSuccess: () => {
            dispatch(
                showSuccessToast(
                    <SuccessToast content={translate("si.contacts_search.contacts.report.toast.success")} />,
                    Date.now(),
                    TOAST_DURATION_MS,
                ),
            );
            onSubmit();
        },
        onError: () => {
            dispatch(
                showErrorToast(
                    renderErrorToast({
                        content: translate("si.contacts_search.contacts.report.toast.error"),
                        minWidth: "auto",
                    }),
                ),
            );
        },
    });

    const visibleReasons = useMemo(
        () => (contact.isRevealed ? [...REVEALED_ONLY_REASONS, ...COMMON_REASONS] : COMMON_REASONS),
        [contact.isRevealed],
    );
    const hasAdditionalInfo = additionalInfo.trim().length > 0;
    const isSubmitDisabled = selectedReasons.length === 0 && !hasAdditionalInfo;

    const handleReasonToggle = (reason: ContactReportReason) => {
        trackingService.trackReportIssueSelectCheckbox(!selectedReasons.includes(reason), reason, contact.id);
        setSelectedReasons((prev) => (prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]));
    };

    const handleTextareaChange = (value: string) => {
        setAdditionalInfo(value);
    };

    const handleSubmit = () => {
        const shouldAddOtherReason = hasAdditionalInfo && !selectedReasons.includes(ContactReportReason.Other);
        const IssueTypes = shouldAddOtherReason ? [...selectedReasons, ContactReportReason.Other] : selectedReasons;

        if (hasAdditionalInfo) {
            trackingService.trackReportIssueOther(additionalInfo);
        }
        trackingService.trackReportIssueSubmit(IssueTypes, contact, additionalInfo);

        const payload: ContactIssuePayload = {
            IssueTypes,
            issueDetails: additionalInfo || undefined,
            contactId: contact.id,
            companyId: contact.companyId,
            companyName: contact.companyName,
            companyDomain: contact.site,
            jobTitle: contact.title,
        };

        if (contact.isRevealed && contact.privateData) {
            payload.email = contact.privateData.emails?.[0];
            payload.mobilePhone = contact.privateData.phoneNumbers?.find((p) => p.internalType === "mobile")?.number;
            payload.directPhone = contact.privateData.phoneNumbers?.find((p) => p.internalType === "direct")?.number;
        }

        submitContactIssue(payload);
        handleClose();
    };

    const handleClose = () => {
        setSelectedReasons([]);
        setAdditionalInfo("");
        onClose();
    };

    const handleCancel = () => {
        trackingService.trackReportIssueCancel();
        handleClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleCancel}
            shouldCloseOnEsc
            shouldCloseOnOverlayClick
            customStyles={{
                content: {
                    width: "400px",
                },
            }}
        >
            <Modal.Header withBorder>
                <Modal.Title data-automation="contact-issue-modal.title">
                    {translate("si.contacts_search.contacts.report.modal.title")}
                </Modal.Title>
            </Modal.Header>

            <Modal.Content>
                <Text
                    fontVariant="body1"
                    color={colorsPalettes.carbon[400]}
                    margin="16px 0"
                    data-automation="contact-issue-modal.subtitle"
                >
                    {translate("si.contacts_search.contacts.report.modal.subtitle")}
                </Text>

                <Flex
                    flexDirection="column"
                    gap="8px"
                    margin="0 0 16px"
                >
                    {visibleReasons.map(({ reason, labelKey }) => (
                        <Flex
                            key={reason}
                            data-automation={`contact-issue-modal.checkbox.${reason}`}
                        >
                            <Checkbox
                                selected={selectedReasons.includes(reason)}
                                label={translate(labelKey)}
                                onClick={() => handleReasonToggle(reason)}
                            />
                        </Flex>
                    ))}
                </Flex>
                <Flex
                    flexDirection="column"
                    margin="0 0 8px"
                >
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                        display="block"
                        margin="0 0 16px"
                        as="label"
                        htmlFor="additional-info-textarea"
                    >
                        {translate("si.contacts_search.contacts.report.other.label")}
                    </Text>
                    <Textarea
                        id="additional-info-textarea"
                        value={additionalInfo}
                        onChange={handleTextareaChange}
                        placeholder={translate("si.contacts_search.contacts.report.other.placeholder")}
                        width={352}
                    />
                </Flex>
            </Modal.Content>

            <Modal.Footer withBorder>
                <Button
                    type="flat"
                    onClick={handleCancel}
                    dataAutomation="contact-issue-modal.button.cancel"
                >
                    {translate("common.cancel")}
                </Button>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    isDisabled={isSubmitDisabled}
                    dataAutomation="contact-issue-modal.button.submit"
                >
                    {translate("common.submit")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ContactIssueModal;
